import React, { useEffect, useRef, useState, memo } from 'react';
import { clsx } from 'clsx';
import { Users } from 'lucide-react';
import SectionShell from './ui/Section';
import { SectionHeader } from './ui/SectionHeader';

const zeroClubFor = [
  'Founders who want real support',
  'Freelancers who want clarity and clients',
  'Creatives who want the right circle',
  'Professionals who want meaningful networking',
  'Students who want a strong start',
];

function useInView<T extends HTMLElement>(rootMargin = '0px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return { ref, inView };
}

const AboutSection = memo(function AboutSection() {
  const a1 = useInView<HTMLElement>('100px');
  const a2 = useInView<HTMLElement>('100px');

  return (
    <SectionShell bgClass="bg-navy-top" id="about">
      <SectionHeader
        eyebrowText="Who We Are"
        eyebrowIcon={Users}
        title="About Us"
        subtitle="A global space for ambitious people to connect, learn, and grow together."
        titleGradientClassName="bg-gradient-to-r from-[#0d273d] via-[#1a3b5c] to-[#d65c30]"
        textColorClass="text-white"
        align="center"
      />

      <div className="mx-auto max-w-6xl space-y-12 sm:space-y-16 [content-visibility:auto] [contain-intrinsic-size:1000px]">

        <article
          ref={a1.ref}
          data-inview={a1.inView ? 'true' : 'false'}
          className={clsx(
            'group relative grid grid-cols-1 md:grid-cols-12 items-center gap-8 rounded-3xl',
            'bg-gradient-to-br from-[#0d273d]/85 via-[#0f2f45]/90 to-[#1a3b5c]/85',
            'border border-white/10',
            '[contain:layout_paint_style] will-change-transform'
          )}
        >
          <div className="absolute inset-y-0 left-0 w-2 bg-gradient-to-b from-[#d65c30] via-[#1a3b5c] to-[#0d273d] opacity-90" />

          <div
            className={clsx(
              'absolute inset-y-0 left-0 w-2 scale-y-110 blur-xl opacity-0 transition-opacity duration-700',
              'group-hover:opacity-70',
              'data-[inview=false]:!blur-0'
            )}
            style={{ background: 'linear-gradient(180deg,#d65c30,#1a3b5c,#0d273d)' }}
          />

          <div className="md:col-span-7 p-8 sm:p-10 lg:p-12">
            <h3 className="font-montserrat font-bold text-3xl sm:text-4xl text-white mb-5 tracking-tight">
              What is ZeroClub?
            </h3>
            <p className="font-montserrat text-base sm:text-lg text-gray-200 leading-relaxed">
              ZeroClub is an English-speaking community for ambitious entrepreneurs, creators, and professionals.
              We bring together people who turn ideas into action through expert talks, workshops, and curated networking.
              <br /><br />
              Our mission is to give international people a place to belong, connect, and grow together
            </p>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative rounded-r-3xl overflow-hidden ring-1 ring-white/20 hidden sm:block">
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <img
                src="/2sec-peoples.jpg"
                alt="Zero Club Community"
                className="w-full h-[400px] object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 rounded-r-3xl p-[3px] pointer-events-none">
                <div
                  className={clsx(
                    'h-full w-full rounded-r-[calc(1.5rem-3px)] transition-opacity duration-500',
                    a1.inView ? 'opacity-100' : 'opacity-0'
                  )}
                  style={{
                    background:
                      'conic-gradient(from 140deg,#d65c30,#1a3b5c,#0d273d,#1a3b5c,#d65c30)',
                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude'
                  }}
                />
              </div>
            </div>
          </div>
        </article>

        <article
          ref={a2.ref}
          data-inview={a2.inView ? 'true' : 'false'}
          className={clsx(
            'group relative grid grid-cols-1 md:grid-cols-12 items-center gap-8 rounded-3xl',
            'bg-gradient-to-br from-[#1a3b5c]/85 via-[#102f49]/90 to-[#0d273d]/85',
            'border border-white/10',
            '[contain:layout_paint_style] will-change-transform'
          )}
        >
          <div className="absolute inset-y-0 right-0 w-2 bg-gradient-to-b from-[#0d273d] via-[#1a3b5c] to-[#d65c30] opacity-90" />

          <div
            className={clsx(
              'absolute inset-y-0 right-0 w-2 scale-y-110 blur-xl opacity-0 transition-opacity duration-700',
              'group-hover:opacity-70',
              'data-[inview=false]:!blur-0'
            )}
            style={{ background: 'linear-gradient(180deg,#0d273d,#1a3b5c,#d65c30)' }}
          />

          <div className="md:col-span-5 relative order-2 md:order-1">
            <div className="relative rounded-l-3xl overflow-hidden ring-1 ring-white/20 hidden sm:block">
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <img
                src="/2sec-for.jpg"
                alt="Zero Club Community"
                className="w-full h-[400px] object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 rounded-l-3xl p-[3px] pointer-events-none">
                <div
                  className={clsx(
                    'h-full w-full rounded-l-[calc(1.5rem-3px)] transition-opacity duration-500',
                    a2.inView ? 'opacity-100' : 'opacity-0'
                  )}
                  style={{
                    background:
                      'conic-gradient(from 250deg,#0d273d,#1a3b5c,#d65c30,#1a3b5c,#0d273d)',
                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="md:col-span-7 p-8 sm:p-10 lg:p-12 order-1 md:order-2">
            <h3 className="font-montserrat font-bold text-3xl sm:text-4xl text-white mb-6 tracking-tight">
              Who is ZeroClub for?
            </h3>
            <ul className="flex flex-wrap gap-3 sm:gap-4">
              {zeroClubFor.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#d65c30] shadow-sm shadow-[#d65c30]/40" />
                  <span className="font-montserrat text-sm sm:text-base text-gray-200">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </article>
      </div>
    </SectionShell>
  );
});

export default AboutSection;
